import { faChevronRight, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import React, { useEffect, useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { recipeThumbnailQuery } from '@/api'
import { NewRecipe, TempRecipe, UserRecipePhoto } from '@/api/types'
import { Button } from '@/components/Button'
import { Spinner } from '@/components/Spinner'
import useRecipePhoto from '../../PhotosView/useRecipePhoto'
import RecipePhotoForm from './RecipePhotoForm'
import RecipePhotoFullscreen from './RecipePhotoFullscreen'
import './RecipePhotoUpload.css'

type Props = {
  recipe: NewRecipe | TempRecipe
  isDisabled?: boolean
}

export default function RecipePhotoUpload({ recipe, isDisabled }: Props) {
  const [photo, setPhoto] = useState<UserRecipePhoto | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const { t } = useTranslation()
  const { deletePhoto, uploadPhoto, approvePhoto, rejectPhoto, setAsCover, isPending } =
    useRecipePhoto(recipe.idRecipe ?? 0)

  const { data: thumbnail, isLoading } = useQuery({
    ...recipeThumbnailQuery(recipe.idRecipe ?? 0),
    enabled: !!recipe.idRecipe,
  })

  // Set existing photo
  useEffect(() => {
    if (thumbnail) {
      setPhoto(thumbnail)
      setPreviewUrl(thumbnail.photoURL)
    }
  }, [thumbnail])

  const handleUpdatePhoto = async (photo: File) => {
    try {
      if (recipe.idRecipe) {
        await uploadPhoto(photo)

        setPreviewUrl(URL.createObjectURL(photo))
      } else {
        toast.error('Please create recipe first')
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const removePhoto = () => {
    setPreviewUrl(null)

    if (photo) {
      deletePhoto(photo.idRecipePhoto)
    }
  }

  return (
    <div className="panel-xs RecipePhotoUpload-panel">
      {isLoading || isPending ? (
        <div className="RecipePhotoUpload-spinner">
          <Spinner />
        </div>
      ) : (
        <div>
          {photo === null || previewUrl === null ? (
            <RecipePhotoForm updatePhoto={handleUpdatePhoto} isDisabled={isDisabled} />
          ) : (
            <div className="RecipePhotoUpload-preview">
              <Button
                className="RecipePhotoUpload-remove"
                onPress={() => {
                  if (confirm(t('photos.confirmReject'))) {
                    removePhoto()
                  }
                }}
                variant="icon">
                <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
              </Button>

              {photo.photoSource === 'AI_GENERATED' && (
                <div className="RecipePhotoItem-ai-badge">AI</div>
              )}

              <DialogTrigger>
                <Button variant="icon" className="RecipePhotoUpload-thumbnail">
                  <img
                    className="RecipePhotoUpload-previewImage"
                    data-in-moderation={photo?.latestModerationStatus === 'NOT_MODERATED'}
                    src={previewUrl}
                  />
                </Button>
                <RecipePhotoFullscreen
                  onApprove={() => approvePhoto(photo.idRecipePhoto)}
                  onReject={() => rejectPhoto(photo.idRecipePhoto)}
                  onSetAsCover={() => setAsCover(photo.idRecipePhoto)}
                  photo={photo}
                  previewUrl={previewUrl}
                />
              </DialogTrigger>
            </div>
          )}
        </div>
      )}

      {recipe.idRecipe && (
        <Link
          className="RecipePhotoUpload-link"
          to="/recipes/$recipeId/photos"
          params={{ recipeId: recipe.idRecipe.toString() }}>
          {t('photos.allPhotos')}
          <FontAwesomeIcon icon={faChevronRight} />
        </Link>
      )}
    </div>
  )
}
